// 剧本生成系统常量配置
// 集中管理所有常量，避免魔法数字和重复定义

import type { ScriptGenerationTaskStatus } from '@/types/scriptGeneration'

/**
 * 任务状态颜色映射
 */
export const STATUS_COLOR: Record<ScriptGenerationTaskStatus, string> = {
  pending: 'default',
  processing: 'processing',
  preprocessing: 'processing',
  validating: 'processing',
  analyzing: 'processing',
  generating_config: 'blue',
  generating_dialogue: 'blue',
  generating_events: 'blue',
  packaging: 'blue',
  paused: 'warning', // 已暂停 - 黄色警告
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
}

/**
 * 任务状态标签映射
 */
/**
 * 任务状态标签映射
 */
export const STATUS_LABEL: Record<ScriptGenerationTaskStatus, string> = {
  pending: '待处理',
  processing: '处理中',
  preprocessing: '预处理中',
  validating: '验证中',
  analyzing: '分析中',
  generating_config: '生成配置',
  generating_dialogue: '生成对话',
  generating_events: '生成事件',
  packaging: '打包中',
  paused: '已暂停', // 新增
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
}

/**
 * 输入类型标签映射
 */
export const INPUT_TYPE_LABEL = {
  text: '文本输入',
  file: '文档上传',
} as const

/**
 * 输入类型
 */
export type InputType = keyof typeof INPUT_TYPE_LABEL

/**
 * 剧本生成配置
 */
export const SCRIPT_GENERATION_CONFIG = {
  /** 文件上传大小限制（字节） */
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB

  /** 允许的文件扩展名 */
  ALLOWED_FILE_EXTENSIONS: ['.txt', '.md', '.doc', '.docx', '.pdf'],

  /** 轮询间隔（毫秒） */
  POLL_INTERVAL: 3000,

  /** 快速轮询间隔（接近完成时） */
  POLL_INTERVAL_FAST: 2000,

  /** 慢速轮询间隔（初期） */
  POLL_INTERVAL_SLOW: 5000,

  /** 最大重试次数 */
  MAX_RETRIES: 3,

  /** 重试延迟（毫秒） */
  RETRY_DELAY: 1000,
} as const

/**
 * 运行中状态列表（用于判断是否应该启动轮询）
 */
export const RUNNING_STATUSES: ScriptGenerationTaskStatus[] = [
  'pending',           // 待处理（任务刚创建时的初始状态）
  'processing',        // 处理中（通用运行状态）
  'preprocessing',     // 预处理中
  'validating',        // 验证中
  'analyzing',         // 分析中
  'generating_config', // 生成配置中
  'generating_dialogue', // 生成对话中
  'generating_events',   // 生成事件中
  'packaging',         // 打包中
]

/**
 * 暂停状态
 */
export const PAUSED_STATUS: ScriptGenerationTaskStatus = 'paused'

/**
 * 终止状态列表（用于判断是否应该停止轮询）
 */
export const TERMINAL_STATUSES: ScriptGenerationTaskStatus[] = [
  'completed',
  'failed',
  'cancelled',
]

/**
 * 可取消状态列表
 */
export const CANCELLABLE_STATUSES: ScriptGenerationTaskStatus[] = [
  'processing',        // 处理中（通用运行状态）
  'preprocessing',
  'validating',
  'analyzing',
  'generating_config',
  'generating_dialogue',
  'generating_events',
  'packaging',
]

/**
 * 可重试状态列表
 */
export const RETRYABLE_STATUSES: ScriptGenerationTaskStatus[] = ['failed', 'cancelled']

/**
 * 可删除状态列表
 */
export const DELETABLE_STATUSES: ScriptGenerationTaskStatus[] = [
  'pending',
  'failed',
  'completed',
  'cancelled',
]

/**
 * 类型守卫：检查是否为有效的输入类型
 */
export function isValidInputType(value: string): value is InputType {
  return value in INPUT_TYPE_LABEL
}

/**
 * 类型守卫：检查是否为运行中状态
 */
export function isRunningStatus(status: ScriptGenerationTaskStatus): boolean {
  return RUNNING_STATUSES.includes(status)
}

/**
 * 类型守卫：检查是否为终止状态
 */
export function isTerminalStatus(status: ScriptGenerationTaskStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/**
 * 类型守卫：检查是否为可取消状态
 */
export function isCancellableStatus(status: ScriptGenerationTaskStatus): boolean {
  return CANCELLABLE_STATUSES.includes(status)
}

/**
 * 类型守卫：检查是否为可重试状态
 */
export function isRetryableStatus(status: ScriptGenerationTaskStatus): boolean {
  return RETRYABLE_STATUSES.includes(status)
}

/**
 * 类型守卫：检查是否为可删除状态
 */
export function isDeletableStatus(status: ScriptGenerationTaskStatus): boolean {
  return DELETABLE_STATUSES.includes(status)
}

/**
 * 类型守卫：检查是否为有效的阶段状态
 */
export function isValidPhaseStatus(
  status: string
): status is 'running' | 'completed' | 'failed' {
  return ['running', 'completed', 'failed'].includes(status)
}
